/**
 * Axios Instance with Token Refresh Interceptor
 *
 * Centralized axios instance that automatically:
 * - Attaches access tokens to all requests
 * - Detects 401 errors (token expired)
 * - Attempts silent token refresh
 * - Retries failed requests with new token
 * - Redirects to login if refresh fails
 */

import axios from 'axios';
import { getValidAccessToken } from './msalService';

// Create axios instance with base configuration
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    timeout: 300000, // 5 minutes timeout
    headers: {
        'Content-Type': 'application/json',
    }
});

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

/**
 * Process queued requests after token refresh
 * @param {Error|null} error - Error if refresh failed
 * @param {string|null} token - New access token if successful
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * Request Interceptor
 * Automatically attaches access token to all outgoing requests
 */
axiosInstance.interceptors.request.use(
    async (config) => {
        // Get token from localStorage
        const token = localStorage.getItem('accessToken');

        // Check if this is an approval API request (not vendor API)
        const isApprovalAPI = config.url && config.url.includes('approvals/v1');
        if (token && isApprovalAPI) {
            // Attach token to Authorization header
            config.headers.Authorization = `Bearer ${token}`;
        } else if (isApprovalAPI) {
            // No token for approval API, might need to login
            console.warn('No access token found for approval API request');
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor
 * Handles 401 errors by attempting token refresh and retrying the request
 */
axiosInstance.interceptors.response.use(
    (response) => {
        // Return successful response as-is
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Check if error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {

            // Check if this is an approval API request
            const isApprovalAPI = originalRequest.url && originalRequest.url.includes('approvals/v1');

            if (!isApprovalAPI) {
                // Not an approval API, don't attempt MSAL refresh
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Token refresh already in progress, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            // Mark that we've tried to refresh for this request
            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Attempt to get a valid access token (will refresh if needed)
                const newToken = await getValidAccessToken();

                if (newToken) {
                    // Token refresh successful
                    console.log('Token refreshed successfully');

                    // Update the original request with new token
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;

                    // Process all queued requests
                    processQueue(null, newToken);

                    // Retry the original request
                    return axiosInstance(originalRequest);
                } else {
                    // Token refresh failed, user needs to login
                    console.error('Token refresh failed, redirecting to login');

                    // Clear auth data
                    localStorage.clear();
                    localStorage.setItem('isLoggedin', 'false');

                    // Process queue with error
                    processQueue(new Error('Token refresh failed'), null);

                    // Redirect to session expired page
                    window.location.href = '/approval/session-expired';

                    return Promise.reject(error);
                }

            } catch (refreshError) {
                console.error('Error during token refresh:', refreshError);

                // Clear auth data
                localStorage.clear();
                localStorage.setItem('isLoggedin', 'false');

                // Process queue with error
                processQueue(refreshError, null);

                // Redirect to session expired page
                window.location.href = '/approval/session-expired';

                return Promise.reject(refreshError);

            } finally {
                isRefreshing = false;
            }
        }

        // For other errors or if retry already attempted, reject
        return Promise.reject(error);
    }
);

export default axiosInstance;
